import PublicAliasConfig from "../components/PublicAliasConfig";
import BrandMark from "../components/brand/BrandMark";
import PauseNoticesConfig from "../components/PauseNoticesConfig";
import RotateQRConfig from "../components/RotateQRConfig";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useAppBack } from "../hooks/useAppBack";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { 
  ArrowLeft, QrCode, Download, CreditCard, 
  Phone, User, CheckCircle, Clock, XCircle, AlertCircle,
  FileText, Image, Trash2, Lock, Hash, Edit, Loader2,
  Palette, RefreshCw, Eye, X, ZoomIn, ZoomOut, Bell
} from "lucide-react";
// V5: Use the new enhanced Sticker Builder Pro
import StickerBuilderProV5 from "../components/stickers/StickerBuilderProV5";
import RecipientsCard from "../components/protections/RecipientsCard";
// V6.3: Import sticker generator for direct downloads
import { generateSticker, fetchLatestQR } from "../lib/sticker_generator_v5";

// P1 (corte final) — vocabulario tipado por protection_type en la ficha del dueño
const PLACE_HINT = {
  vehicle: "colócalo en tu vehículo",
  kid: "colócalo en la mochila o prenda del niño/a",
  pet: "colócalo en el collar o placa de tu mascota",
  object: "colócalo en el objeto que quieres proteger",
  other: "colócalo en lo que quieres proteger",
};
// V8.3: Dynamic Edit Protection Modal
import EditProtectionModal from "../components/edit/EditProtectionModal";
import { protectionTypes } from "../lib/designTokens";
import { useI18n } from "../i18n";
import { userLocale } from "../utils/locale";
import { localizeSavedTexts } from "../data/stickerSavedI18n";

const API = '/api';

const VehicleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  // RC-WEB-5 GATE 13 — Back returns to the real previous route (Protecciones
  // when the user came from the list) instead of always jumping to Resumen.
  const goBack = useAppBack("/dashboard/vehicles");
  const { t, lang } = useI18n();
  const [vehicle, setVehicle] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // V6.2: Saved sticker config state
  const [savedSticker, setSavedSticker] = useState(null);
  // 4-D2: textos guardados sin personalizar se muestran/descargan en el idioma del dueño
  const savedTexts = useMemo(() => localizeSavedTexts(
    savedSticker?.config?.texts || {
      title: savedSticker?.config?.title,
      subtitle: savedSticker?.config?.subtitle,
      slogan: savedSticker?.config?.slogan,
    },
    savedSticker?.config?.template_id,
    lang
  ), [savedSticker, lang]);
  const [loadingSticker, setLoadingSticker] = useState(false);
  
  // V6.2: View sticker modal state
  const [viewStickerModalOpen, setViewStickerModalOpen] = useState(false);
  
  // V6.3: Download state for direct downloads
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadFormat, setDownloadFormat] = useState(null);
  
  // Track previous status to detect transitions
  const prevStatusRef = useRef(null);
  const isFirstLoad = useRef(true);
  
  // QR refresh key - changes this to force QR image reload
  const [qrRefreshKey, setQrRefreshKey] = useState(Date.now());

  // iter72 1.1 — banner post-rotación (D1: llevar al dueño al sticker nuevo)
  const [rotatedBanner, setRotatedBanner] = useState(false);
  
  // Sticker selector modal state - V3
  const [stickerModalOpen, setStickerModalOpen] = useState(false);
  
  // Edit state
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  // V6.2: Fetch saved sticker config (with version for cache busting)
  const fetchSavedSticker = async () => {
    setLoadingSticker(true);
    try {
      const response = await axios.get(`${API}/user/sticker-config/${id}`);
      if (response.data && (response.data.config || response.data.found)) {
        // Extract config and metadata
        const stickerData = {
          config: response.data.config,
          version: response.data.config?.version || response.data.version || 1,
          updated_at: response.data.config?.updated_at || response.data.updated_at
        };
        setSavedSticker(stickerData);
      }
    } catch (error) {
      // No saved sticker yet - that's okay
      console.log("No saved sticker found for vehicle:", id);
    } finally {
      setLoadingSticker(false);
    }
  };

  // V6.3: Direct download function using saved sticker config
  const handleDirectDownload = useCallback(async (format) => {
    if (!savedSticker?.config) {
      toast.error("No hay sticker guardado para descargar");
      return;
    }
    
    setIsDownloading(true);
    setDownloadFormat(format);
    
    try {
      const config = savedSticker.config;
      const locTexts = localizeSavedTexts(
        config.texts || { title: config.title, subtitle: config.subtitle, slogan: config.slogan },
        config.template_id,
        lang
      );
      
      // V6.3: Build styleOptions from saved config
      const styleOptions = {
        title: locTexts.title || vehicle?.name || "",
        subtitle: locTexts.subtitle || "",
        slogan: locTexts.slogan || "",
        titleSize: config.text_sizes?.title || "L",
        subtitleSize: config.text_sizes?.subtitle || "M",
        sloganSize: config.text_sizes?.slogan || "M",
        backgroundGradient: config.colors?.background_gradient || config.backgroundGradient,
        primaryTextColor: config.colors?.primary_text || config.primaryTextColor,
        secondaryTextColor: config.colors?.secondary_text || config.secondaryTextColor,
        activeIcons: config.icons?.active || config.activeIcons || [],
        showIcons: config.icons?.show !== false && config.showIcons !== false,
        iconPosition: config.icons?.position || config.iconPosition || "top-right",
        characterEnabled: config.effects?.character_enabled || config.characterEnabled || false,
        characterId: config.effects?.character_id || config.characterId || null,
        frameEnabled: config.effects?.frame_enabled !== false && config.frameEnabled !== false,
        glowEnabled: config.effects?.glow_enabled || config.glowEnabled || false,
        layoutOffsets: config.layout_offsets || config.layoutOffsets || {}
      };
      
      // V6.3: CRITICAL - Pass SAVED texts directly to override any template values
      const result = await generateSticker({
        vehicleId: id,
        template: { 
          id: config.template_id || "custom",
          palette: config.colors?.background_gradient?.palette || "emerald_forest",
          layout: "hero"
        },
        customTitle: styleOptions.title,
        customSubtitle: styleOptions.subtitle,
        customSlogan: styleOptions.slogan,
        styleOptions: styleOptions,
        format: format
      });
      
      if (result.success) {
        toast.success(`¡Sticker ${format.toUpperCase()} descargado!`, { duration: 3000 });
      }
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Error al descargar: " + error.message);
    } finally {
      setIsDownloading(false);
      setDownloadFormat(null);
    }
  }, [savedSticker, id, vehicle, lang]);

  useEffect(() => {
    fetchVehicle();
    fetchPayments();
    fetchSavedSticker();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchVehicle = async () => {
    try {
      const response = await axios.get(`${API}/vehicles/${id}`);
      const vehicleData = response.data;
      
      // Debug log for development
      if (process.env.NODE_ENV !== "production") {
        console.log("Vehicle status debug:", {
          vehicle_id: id,
          qr_status: vehicleData.qr_status,
          computed_qr_status: vehicleData.computed_qr_status,
          payment_status: vehicleData.payment_status,
          computed_payment_status: vehicleData.computed_payment_status,
          has_approved_payment: vehicleData.has_approved_payment
        });
      }
      
      // Detect status transition for toast (only after first load)
      const currentStatus = vehicleData.computed_qr_status || vehicleData.qr_status;
      if (!isFirstLoad.current && prevStatusRef.current && prevStatusRef.current !== "active" && currentStatus === "active") {
        toast.success("¡Tu QR ya está activo!", {
          description: "Ya puedes descargar e imprimir tu código QR"
        });
      }
      
      prevStatusRef.current = currentStatus;
      isFirstLoad.current = false;
      
      setVehicle(vehicleData);
    } catch (error) {
      toast.error("Error al cargar la protección");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const fetchPayments = async () => {
    try {
      const response = await axios.get(`${API}/payments/vehicle/${id}`);
      setPayments(response.data);
    } catch (error) {
      console.error("Error fetching payments:", error);
    }
  };

  const handleDownloadQR = async (format) => {
    try {
      const response = await axios.get(`${API}/vehicles/${id}/qr/${format}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `avisocar-${vehicle.name}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`QR descargado en ${format.toUpperCase()}`);
    } catch (error) {
      const message = error.response?.data?.detail || "Error al descargar QR";
      toast.error(message);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/vehicles/${id}`);
      toast.success("Protección eliminada");
      navigate("/dashboard");
    } catch (error) {
      toast.error("Error al eliminar la protección");
    } finally {
      setDeleting(false);
    }
  };

  // Open edit dialog with current vehicle data
  const openEditDialog = () => {
    // Handle primary_contact being either string or object
    setEditDialogOpen(true);
  };

  // Manual QR refresh - V2
  const handleRefreshQR = async () => {
    try {
      const response = await axios.post(`${API}/qr/regenerate?vehicle_id=${id}`);
      const qrData = response.data.qr;
      
      // Update QR refresh key
      setQrRefreshKey(new Date(qrData?.qr_updated_at || Date.now()).getTime());
      
      toast.success("🔄 QR sincronizado", {
        description: `Versión: ${qrData?.qr_payload_version}`
      });
    } catch (error) {
      toast.error("Error al sincronizar QR");
    }
  };

  // USE COMPUTED STATUS as single source of truth
  const getStatusInfo = () => {
    if (!vehicle) return null;
    
    // Use computed status from backend (server-authoritative)
    const qrStatus = vehicle.computed_qr_status || vehicle.qr_status;
    const paymentStatus = vehicle.computed_payment_status || vehicle.payment_status;
    const hasApprovedPayment = vehicle.has_approved_payment;
    
    // ACTIVE: has approved payment OR qr_status is active
    if (qrStatus === "active" || hasApprovedPayment) {
      return {
        icon: <CheckCircle className="w-6 h-6 text-emerald-500" />,
        badge: <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">{t("owner.vd.qr_active")}</Badge>,
        message: t("owner.vd.qr_active_msg"),
        color: "emerald",
        showQR: true
      };
    }
    
    // IN REVIEW
    if (paymentStatus === "payment_review") {
      return {
        icon: <Clock className="w-6 h-6 text-amber-500" />,
        badge: <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">{t("checkout.history_pending")}</Badge>,
        message: t("checkout.status_review_text"),
        color: "amber",
        showQR: false
      };
    }
    
    // REJECTED
    if (paymentStatus === "rejected") {
      return {
        icon: <XCircle className="w-6 h-6 text-red-500" />,
        badge: <Badge className="bg-red-100 text-red-700 hover:bg-red-100">{t("checkout.status_rejected_title")}</Badge>,
        message: t("owner.vd.rejected_msg"),
        color: "red",
        showQR: false
      };
    }
    
    // PENDING (default)
    return {
      icon: <AlertCircle className="w-6 h-6 text-slate-500" />,
      badge: <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100">{t("owner.status.payment_pending")}</Badge>,
      message: t("owner.vd.pending_msg"),
      color: "slate",
      showQR: false
    };
  };

  // Helper to get primary contact display value
  const getPrimaryContactDisplay = () => {
    if (!vehicle?.primary_contact) return "-";
    if (typeof vehicle.primary_contact === "object") {
      return vehicle.primary_contact.phone || vehicle.primary_contact.whatsapp || "-";
    }
    return vehicle.primary_contact;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1B84FF]"></div>
      </div>
    );
  }

  if (!vehicle) return null;

  const statusInfo = getStatusInfo();

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#F8FAFC]">
      {/* Header - Premium Theme */}
      <header className="bg-white/85 backdrop-blur-xl border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16 gap-3 sm:gap-4">
            <button
              onClick={goBack}
              data-testid="back-to-dashboard"
              aria-label="Volver"
              className="inline-flex h-10 w-10 items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-slate-50 hover:text-[#0B2545]"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <BrandMark size="sm" to="/dashboard" />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto w-full min-w-0 px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Approval Success Banner - Premium */}
        {statusInfo.showQR && (
          <div className="mb-4 sm:mb-6 bg-[#1B84FF]/10 border border-[#1B84FF]/30 rounded-xl p-3 sm:p-4 backdrop-blur-xl" data-testid="approval-banner">
            <div className="flex items-start sm:items-center gap-3">
              <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-[#1B84FF] flex-shrink-0 mt-0.5 sm:mt-0" />
              <div>
                <p className="font-semibold text-[#0B2545] text-sm sm:text-base">Tu pago fue aprobado. Tu QR ya está activo.</p>
                <p className="text-sm text-slate-500">Descarga tu código QR, imprímelo y {PLACE_HINT[vehicle?.protection_type] || PLACE_HINT.vehicle}.</p>
              </div>
            </div>
          </div>
        )}

        {/* Status Banner - Premium */}
        {rotatedBanner && (
          <div className="mb-4 sm:mb-6 bg-amber-500/10 border border-amber-400/40 rounded-xl p-4 backdrop-blur-xl" data-testid="qr-rotated-banner">
            <p className="text-amber-700 text-sm font-bold">🖨️ Tu QR cambió — imprime y reemplaza el anterior</p>
            <p className="text-amber-700/70 text-xs mt-1">
              El QR anterior ya no funciona. Descarga tu nuevo sticker aquí abajo (PNG o PDF) y {PLACE_HINT[vehicle?.protection_type] || PLACE_HINT.vehicle}.
            </p>
          </div>
        )}

        <div className="bg-white shadow-[0_8px_30px_rgba(11,37,69,0.05)] rounded-xl border border-slate-200 p-4 sm:p-6 mb-6" data-testid="vehicle-status-card">
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              statusInfo.color === 'emerald' ? 'bg-emerald-500/20' : 
              statusInfo.color === 'amber' ? 'bg-amber-500/20' : 
              statusInfo.color === 'red' ? 'bg-red-500/20' : 'bg-slate-500/20'
            }`}>
              {statusInfo.icon}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <h2 className="text-lg font-semibold text-[#0B2545]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {vehicle.name}
                </h2>
                {vehicle.plate_number && (
                  <Badge className="bg-slate-100 text-[#0B2545] border border-slate-300">
                    <Hash className="w-3 h-3 mr-1" />
                    {vehicle.plate_number}
                  </Badge>
                )}
                {statusInfo.badge}
              </div>
              <p className="text-slate-500">{statusInfo.message}</p>
            </div>
          </div>
        </div>

        <div className="grid min-w-0 gap-6 md:grid-cols-2">
          {/* QR Code Section - Premium */}
          <div className="bg-white shadow-[0_8px_30px_rgba(11,37,69,0.05)] rounded-xl border border-slate-200">
            <div className="p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-[#0B2545] flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <QrCode className="w-5 h-5 text-[#1B84FF]" />
                    {t("owner.vd.qr_title")}
                  </h3>
                  {statusInfo.showQR && (
                    <p className="text-slate-500 text-sm">
                      {t("owner.vd.qr_scan_hint")}
                    </p>
                  )}
                </div>
                {statusInfo.showQR && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleRefreshQR}
                    className="text-slate-500 hover:text-[#0B2545] hover:bg-slate-50"
                    data-testid="refresh-qr-btn"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                )}
              </div>
              
              {statusInfo.showQR ? (
                <div className="space-y-4">
                  {/* QR Image with anti-cache */}
                  <div className="bg-white rounded-xl p-4 flex items-center justify-center relative">
                    <img 
                      src={`/api/vehicles/${id}/qr/preview?t=${qrRefreshKey}`}
                      alt="QR Code"
                      className="w-48 h-48 object-contain"
                      key={qrRefreshKey}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="w-32 h-32 bg-white rounded-lg items-center justify-center shadow-sm hidden">
                      <QrCode className="w-20 h-20 text-slate-900" />
                    </div>
                    {/* QR Version badge */}
                    {vehicle.qr_payload_version && (
                      <div className="absolute top-2 right-2 bg-slate-900/90 backdrop-blur-sm px-2 py-1 rounded-full text-xs text-white">
                        v{vehicle.qr_payload_version}
                      </div>
                    )}
                  </div>
                  
                  {/* V3: STICKER SELECTOR BUTTON */}
                  <div className="space-y-3">
                    <p className="text-center text-sm text-slate-500">
                      Selecciona para qué usarás tu QR
                    </p>
                    <Button 
                      onClick={() => setStickerModalOpen(true)}
                      className="w-full h-14 rounded-xl bg-[#1B84FF] hover:bg-[#0F6FDB] text-white gap-3 text-lg shadow-lg shadow-[#1B84FF]/30"
                      data-testid="open-sticker-selector-btn"
                    >
                      <Palette className="w-5 h-5" />
                      {t("owner.vd.create_sticker_full")}
                    </Button>
                    
                    {/* Simple download options */}
                    <div className="grid grid-cols-2 gap-2">
                      <Button 
                        onClick={() => handleDownloadQR('png')}
                        variant="outline"
                        size="sm"
                        className="rounded-lg border-slate-300 text-[#0B2545] hover:bg-slate-50"
                        data-testid="download-qr-png-btn"
                      >
                        <Image className="w-3 h-3 mr-1" />
                        {t("owner.vd.only_qr_png")}
                      </Button>
                      <Button 
                        onClick={() => handleDownloadQR('pdf')}
                        variant="outline"
                        size="sm"
                        className="rounded-lg border-slate-300 text-[#0B2545] hover:bg-slate-50"
                        data-testid="download-qr-pdf-btn"
                      >
                        <FileText className="w-3 h-3 mr-1" />
                        {t("owner.vd.only_qr_pdf")}
                      </Button>
                    </div>
                  </div>
                  
                  <div className="bg-[#1B84FF]/10 border border-[#1B84FF]/30 rounded-lg p-4">
                    <h4 className="font-semibold text-[#0B2545] text-sm mb-2">{t("owner.vd.instructions")}</h4>
                    <ul className="text-sm text-slate-600 space-y-1">
                      <li>• {t("owner.vd.inst1")}</li>
                      <li>• {t("owner.vd.inst2")}</li>
                      <li>• {t("owner.vd.inst3")}</li>
                      <li>• {t("owner.vd.inst4")}</li>
                    </ul>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Lock className="w-10 h-10 text-slate-500" />
                  </div>
                  <h3 className="font-semibold text-[#0B2545] mb-2">{t("owner.vd.qr_unavailable")}</h3>
                  <p className="text-slate-500 text-sm mb-4">
                    {vehicle.payment_status === "payment_review" 
                      ? t("owner.vd.qr_after_approve")
                      : t("owner.vd.qr_after_pay")
                    }
                  </p>
                  {vehicle.payment_status !== "payment_review" && (
                    <Link to={`/checkout/${vehicle.id}`}>
                      <Button 
                        className="rounded-full bg-[#1B84FF] hover:bg-[#0F6FDB] text-white shadow-lg shadow-[#1B84FF]/30"
                        data-testid="go-to-payment-btn"
                      >
                        <CreditCard className="w-4 h-4 mr-2" />
                        {t("owner.vd.make_payment")}
                      </Button>
                    </Link>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* V6.2: My Saved Sticker Card - REAL PREVIEW + VIEW BUTTON - Premium */}
          <div className="bg-white shadow-[0_8px_30px_rgba(11,37,69,0.05)] rounded-xl border border-slate-200" data-testid="saved-sticker-card">
            <div className="p-4 sm:p-6">
              <h3 className="text-lg font-semibold text-[#0B2545] flex items-center gap-2 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Palette className="w-5 h-5 text-[#1B84FF]" />
                {t("owner.vd.my_sticker")}
              </h3>
              <p className="text-slate-500 text-sm mb-4">
                {t("owner.vd.my_sticker_desc")}
              </p>
              
              {loadingSticker ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-[#1B84FF]" />
                </div>
              ) : savedSticker ? (
                <div className="space-y-4">
                  {/* V6.2 FIX: Config-based preview (no server image - auth issues) */}
                  <div 
                    className="relative w-full rounded-2xl overflow-hidden cursor-pointer group"
                    style={{ minHeight: '220px' }}
                    onClick={() => setViewStickerModalOpen(true)}
                  >
                    {/* V6.2: Real preview based on saved config */}
                    <div 
                      className="w-full h-56 rounded-2xl flex flex-col items-center justify-center relative overflow-hidden"
                      style={{ 
                        background: `linear-gradient(135deg, ${
                          savedSticker.config?.colors?.background_gradient?.colors?.[0] || 
                          savedSticker.config?.backgroundGradient?.colors?.[0] || 
                          '#10B981'
                        } 0%, ${
                          savedSticker.config?.colors?.background_gradient?.colors?.[1] || 
                          savedSticker.config?.backgroundGradient?.colors?.[1] || 
                          '#059669'
                        } 100%)`
                      }}
                    >
                      {/* Frame border */}
                      <div className="absolute inset-2 border-2 border-white/25 rounded-xl pointer-events-none" />
                      
                      {/* Saved Sticker Content */}
                      <div className="text-center text-[#0B2545] px-4 z-10">
                        {/* Title */}
                        <h4 className="font-extrabold text-xl line-clamp-2 drop-shadow-md mb-1">
                          {savedTexts.title || vehicle?.name}
                        </h4>
                        {/* Subtitle */}
                        {savedTexts.subtitle && (
                          <p className="text-sm text-slate-600 mb-1">
                            {savedTexts.subtitle}
                          </p>
                        )}
                        {/* Slogan */}
                        {savedTexts.slogan && (
                          <p className="text-xs text-white/90 font-medium mb-2">
                            {savedTexts.slogan}
                          </p>
                        )}
                        {/* QR placeholder */}
                        <div className="bg-white rounded-xl p-2 inline-block shadow-lg">
                          <QrCode className="w-12 h-12 text-slate-800" />
                        </div>
                        {/* Branding */}
                        <p className="text-sm mt-2 font-bold">🚗 AvisoCar.com</p>
                      </div>
                      
                      {/* V6.4: Show emoji if enabled - with SAVED POSITION */}
                      {(savedSticker.config?.effects?.character_enabled || savedSticker.config?.characterEnabled) && 
                       (savedSticker.config?.effects?.character_id || savedSticker.config?.characterId) && (
                        <div 
                          className="absolute text-3xl"
                          style={{
                            // V6.4: Use saved position if available, otherwise default
                            left: savedSticker.config?.layout_offsets?.emoji?.xPercent !== undefined 
                              ? `${savedSticker.config.layout_offsets.emoji.xPercent * 100}%`
                              : savedSticker.config?.layoutOffsets?.emoji?.xPercent !== undefined
                                ? `${savedSticker.config.layoutOffsets.emoji.xPercent * 100}%`
                                : 'auto',
                            top: savedSticker.config?.layout_offsets?.emoji?.yPercent !== undefined
                              ? `${savedSticker.config.layout_offsets.emoji.yPercent * 100}%`
                              : savedSticker.config?.layoutOffsets?.emoji?.yPercent !== undefined
                                ? `${savedSticker.config.layoutOffsets.emoji.yPercent * 100}%`
                                : 'auto',
                            right: (savedSticker.config?.layout_offsets?.emoji?.xPercent === undefined && 
                                   savedSticker.config?.layoutOffsets?.emoji?.xPercent === undefined) ? '16px' : 'auto',
                            bottom: (savedSticker.config?.layout_offsets?.emoji?.yPercent === undefined &&
                                    savedSticker.config?.layoutOffsets?.emoji?.yPercent === undefined) ? '48px' : 'auto'
                          }}
                        >
                          {(() => {
                            const charId = savedSticker.config?.effects?.character_id || savedSticker.config?.characterId;
                            // V6.5: Complete emoji map matching stickerCharacters.js
                            const emojiMap = {
                              // Creativos
                              'hero_smile': '😎', 'oops_face': '😅', 'wink': '😉', 'thinking': '🤔', 'star_eyes': '🤩',
                              // Vehículos
                              'car_red': '🚗', 'car_sport': '🏎️', 'moto': '🏍️', 'truck': '🚚', 'parking': '🅿️',
                              // Emergencia
                              'siren': '🚨', 'ambulance': '🚑', 'warning': '⚠️', 'sos': '🆘', 'medical': '🏥',
                              // Mascotas
                              'dog': '🐕', 'cat': '🐱', 'paw': '🐾', 'puppy_face': '🐶',
                              // Niños
                              'child': '🧒', 'family': '👨‍👩‍👧', 'baby': '👶', 'angel': '👼',
                              // Viajes
                              'plane': '✈️', 'luggage': '🧳', 'backpack': '🎒', 'world': '🌍',
                              // Objetos
                              'key': '🔑', 'phone': '📱', 'gift': '🎁', 'heart': '❤️',
                              // Privacidad
                              'lock': '🔒', 'shield': '🛡️', 'spy': '🕵️',
                              // LATAM
                              'peru_flag': '🇵🇪', 'handshake': '🤝', 'fist': '👊', 'call_me': '🤙',
                              // Legacy IDs (backwards compatibility)
                              'car_wink': '🚗', 'car_cool': '😎', 'car_happy': '😊', 'hand_wave': '👋',
                              'sparkles': '✨', 'star': '⭐', 'thumbsup': '👍', 'smile': '😊',
                              'fire': '🔥', 'rocket': '🚀', 'check': '✅'
                            };
                            return emojiMap[charId] || '🚗';
                          })()}
                        </div>
                      )}
                      
                      {/* V6.4: Show icons if enabled - with SAVED POSITION */}
                      {(savedSticker.config?.icons?.show !== false && savedSticker.config?.showIcons !== false) && 
                       (savedSticker.config?.icons?.active?.length > 0 || savedSticker.config?.activeIcons?.length > 0) && (
                        <div 
                          className="absolute flex gap-1 bg-white/20 backdrop-blur-sm px-2 py-1 rounded-lg"
                          style={{
                            // V6.4: Use saved position if available
                            left: savedSticker.config?.layout_offsets?.icons?.xPercent !== undefined 
                              ? `${savedSticker.config.layout_offsets.icons.xPercent * 100}%`
                              : savedSticker.config?.layoutOffsets?.icons?.xPercent !== undefined
                                ? `${savedSticker.config.layoutOffsets.icons.xPercent * 100}%`
                                : 'auto',
                            top: savedSticker.config?.layout_offsets?.icons?.yPercent !== undefined
                              ? `${savedSticker.config.layout_offsets.icons.yPercent * 100}%`
                              : savedSticker.config?.layoutOffsets?.icons?.yPercent !== undefined
                                ? `${savedSticker.config.layoutOffsets.icons.yPercent * 100}%`
                                : '16px',
                            right: (savedSticker.config?.layout_offsets?.icons?.xPercent === undefined && 
                                   savedSticker.config?.layoutOffsets?.icons?.xPercent === undefined) ? '16px' : 'auto'
                          }}
                        >
                          {(savedSticker.config?.icons?.active || savedSticker.config?.activeIcons || []).slice(0, 3).map((iconId, i) => {
                            // V6.3: Complete icon map matching editor's PREMIUM_ICONS
                            const iconMap = {
                              // Vehiculos
                              'car': '🚗', 'tow': '🚛', 'parking': '🅿️', 'lights': '🚨', 'alarm': '🔔',
                              // Niños
                              'child': '🧒', 'family': '👨‍👩‍👧', 'school': '🎒', 'park': '🎠', 'bracelet': '📿',
                              // Mascotas
                              'paw': '🐾', 'collar': '🦮', 'pet_home': '🏠', 'vet': '💉', 'bone': '🦴',
                              // Objetos
                              'luggage': '🧳', 'airport': '✈️', 'backpack': '🎒', 'keys': '🔑', 'wallet': '👛',
                              // Viajes
                              'plane': '✈️', 'passport': '🛂', 'map': '🗺️', 'hotel': '🏨', 'ticket': '🎫',
                              // Emergencias
                              'sos': '🆘', 'ambulance': '🚑', 'crash': '💥', 'police': '🚔', 'fire': '🔥',
                              // Privacidad
                              'lock': '🔒', 'shield': '🛡️', 'anonymous': '🎭', 'eye_off': '🙈', 'secure': '🔐',
                              // Legacy icons
                              'motorcycle': '🏍️', 'truck': '🚛', 'bike': '🚲', 'bus': '🚌',
                              'heart': '❤️', 'star': '⭐', 'lightning': '⚡', 'sun': '☀️', 'moon': '🌙',
                              'key': '🔑', 'bell': '🔔'
                            };
                            return <span key={i} className="text-lg">{iconMap[iconId] || '⭐'}</span>;
                          })}
                        </div>
                      )}
                    </div>
                    
                    {/* V6.2: Hover overlay with "Ver mi Sticker" */}
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-2xl">
                      <div className="text-white flex items-center gap-2 bg-[#1B84FF] px-4 py-2 rounded-full shadow-lg">
                        <Eye className="w-5 h-5" />
                        <span className="font-semibold">{t("vd_sticker.view")}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* V6.2: "Ver mi Sticker" prominent button */}
                  <Button 
                    onClick={() => setViewStickerModalOpen(true)}
                    className="w-full rounded-lg bg-[#1B84FF] hover:bg-[#0F6FDB] text-white gap-2 h-11 font-semibold shadow-lg shadow-[#1B84FF]/30"
                    data-testid="view-sticker-btn"
                  >
                    <Eye className="w-5 h-5" />
                    {t("vd_sticker.view_full")}
                  </Button>
                  
                  {/* Sticker Info */}
                  <div className="bg-white border border-slate-200 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-slate-500">
                        {t("vd_sticker.last_edited")}: {savedSticker.updated_at 
                          ? new Date(savedSticker.updated_at).toLocaleDateString(userLocale(lang), { 
                              day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' 
                            })
                          : t("vd_sticker.just_created")
                        }
                      </p>
                      {savedSticker.version && (
                        <span className="text-[10px] text-[#1B84FF] bg-[#1B84FF]/20 px-2 py-0.5 rounded font-medium">
                          v{savedSticker.version}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="grid grid-cols-2 gap-2">
                    <Button 
                      onClick={() => setStickerModalOpen(true)}
                      variant="outline"
                      className="rounded-lg gap-2 border-slate-300 text-[#0B2545] hover:bg-slate-50"
                      data-testid="edit-saved-sticker-btn"
                    >
                      <Edit className="w-4 h-4" />
                      {t("owner.vd.edit")}
                    </Button>
                    <Button 
                      onClick={() => handleDirectDownload('png')}
                      disabled={isDownloading || !!vehicle?.qr_locked}
                      className="rounded-lg gap-2 bg-[#1B84FF] hover:bg-[#0F6FDB] text-white disabled:opacity-50"
                      data-testid="download-saved-sticker-btn"
                    >
                      {isDownloading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4" />
                      )}
                      {t("owner.vd.download")}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-6">
                  <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Palette className="w-8 h-8 text-slate-500" />
                  </div>
                  <h4 className="font-medium text-[#0B2545] mb-2">{t("owner.vd.no_design_title")}</h4>
                  <p className="text-sm text-slate-500 mb-4">
                    {t("owner.vd.no_design_text")}
                  </p>
                  <Button 
                    onClick={() => setStickerModalOpen(true)}
                    className="rounded-full bg-[#1B84FF] hover:bg-[#0F6FDB] text-white shadow-lg shadow-[#1B84FF]/30"
                    data-testid="create-sticker-btn"
                  >
                    <Palette className="w-4 h-4 mr-2" />
                    {t("owner.vd.create_sticker")}
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Contact Info - Premium */}
          <RecipientsCard profileId={id} />
        </div>

        {/* Payment History - Premium */}
        {payments.length > 0 && (
          <div className="bg-white shadow-[0_8px_30px_rgba(11,37,69,0.05)] rounded-xl border border-slate-200 mt-6">
            <div className="p-4 sm:p-6">
              <h3 className="text-lg font-semibold text-[#0B2545] flex items-center gap-2 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <CreditCard className="w-5 h-5 text-[#1B84FF]" />
                Historial de Pagos
              </h3>
              <div className="space-y-3">
                {payments.map((payment) => (
                  <div key={payment.id} className="flex items-center justify-between p-4 bg-white border border-slate-200 rounded-lg">
                    <div>
                      <p className="font-semibold text-[#0B2545]">S/ {payment.amount}</p>
                      <p className="text-sm text-slate-500">
                        {payment.method.toUpperCase()} - {new Date(payment.created_at).toLocaleDateString(userLocale(lang))}
                      </p>
                      {payment.rejection_reason && (
                        <p className="text-sm text-red-400 mt-1">{t("vd_sticker.reason")}: {payment.rejection_reason}</p>
                      )}
                    </div>
                    <Badge className={
                      payment.status === "approved" ? "bg-[#1B84FF]/20 text-[#1B84FF] border border-[#1B84FF]/30" :
                      payment.status === "pending" ? "bg-amber-500/20 text-amber-400 border border-amber-500/30" :
                      "bg-red-500/20 text-red-400 border border-red-500/30"
                    }>
                      {payment.status === "approved" ? "Aprobado" :
                       payment.status === "pending" ? "En Revisión" : "Rechazado"}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* RC-WEB-4 §11/§24 — security action: rotating the QR only invalidates the
            printed code. Profile, contacts, history and payments stay the same. */}
        <div className="mt-6 rounded-xl border border-amber-300 bg-amber-50 p-4 sm:p-6" data-testid="qr-security-card">
          <h3 className="font-semibold text-amber-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Rotar el código QR
          </h3>
          <p className="mt-1 text-sm text-amber-900/90">
            Úsalo solo si quieres invalidar el QR impreso (por ejemplo, si lo perdiste o lo copiaron).
            <strong> El QR impreso actual dejará de funcionar. Deberás imprimir el nuevo código.</strong>
          </p>
          <p className="mt-2 text-xs font-semibold text-amber-900" data-testid="qr-rotation-sticker-warning">
            {t("app.protection.rotate_sticker_warning")}
          </p>
          <p className="mt-2 text-xs text-amber-900/80" data-testid="qr-rotation-invariant-note">
            Tus contactos de confianza, tu historial de mensajes y llamadas, tu membresía y tu sticker
            se conservan. Cambiar tu teléfono o tus contactos NO requiere rotar el QR.
          </p>
          <Button
            variant="outline"
            className="mt-4 border-amber-400 text-amber-900 hover:bg-amber-100"
            data-testid="rotate-qr-btn"
            onClick={async () => {
              if (!window.confirm(t("app.protection.rotate_sticker_warning"))) return;
              try {
                await axios.post(`${API}/vehicles/${id}/rotate-qr`, { confirm: true });
                toast.success(t("app.protection.rotate_sticker_done"));
                setRotatedBanner(true);
                fetchVehicle();
              } catch (e) {
                toast.error(e.response?.data?.detail || "No se pudo rotar el QR");
              }
            }}
          >
            <RefreshCw className="mr-2 h-4 w-4" /> Rotar QR
          </Button>
        </div>

        {/* Delete Section - Premium */}
        <div className="bg-red-500/10 backdrop-blur-xl rounded-xl border border-red-500/30 mt-6">
          <div className="p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-red-400" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {t("owner.vd.delete_title")}
                </h3>
                <p className="text-sm text-red-400/80">
                  {t("owner.vd.delete_warn")}
                </p>
              </div>
              <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <Button 
                  variant="outline" 
                  className="text-red-400 border-red-500/30 hover:bg-red-500/20" 
                  onClick={() => setDeleteDialogOpen(true)}
                  data-testid="delete-vehicle-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  {t("owner.vd.delete_btn")}
                </Button>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{t("owner.vd.delete_confirm_title")}</DialogTitle>
                    <DialogDescription>
                      {t("owner.vd.delete_confirm_text", { name: vehicle.name })}
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                      {t("owner.vd.cancel")}
                    </Button>
                    <Button 
                      variant="destructive" 
                      onClick={handleDelete}
                      disabled={deleting}
                      data-testid="confirm-delete-btn"
                    >
                      {deleting ? t("owner.vd.deleting") : t("owner.vd.delete_btn")}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </main>

      {/* V8.3: Dynamic Edit Protection Modal */}
      <EditProtectionModal
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        protection={vehicle}
        onSaved={(updatedData) => {
          // Update local state with new data
          setVehicle(prev => ({ ...prev, ...updatedData }));
          // Refresh QR to reflect any contact changes
          setQrRefreshKey(Date.now());
          // Refresh saved sticker in case data changed
          fetchSavedSticker();
        }}
      />

      {/* V5: Sticker Builder Pro Modal */}
      <StickerBuilderProV5
        isOpen={stickerModalOpen}
        onClose={() => {
          setStickerModalOpen(false);
          // V6.2: Refresh saved sticker after editing
          fetchSavedSticker();
        }}
        vehicleId={id}
        vehicleName={vehicle?.name || ""}
      />
      
      {/* V6.2: View My Sticker Modal */}
      <Dialog open={viewStickerModalOpen} onOpenChange={setViewStickerModalOpen}>
        <DialogContent className="max-w-3xl w-[95vw] p-0 overflow-hidden">
          <div className="relative bg-slate-900">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-700">
              <div>
                <h2 className="text-lg font-bold text-[#0B2545]">{t("vd_sticker.my_sticker")}</h2>
                <p className="text-xs text-slate-500">{vehicle?.name}</p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setViewStickerModalOpen(false);
                    setStickerModalOpen(true);
                  }}
                  className="text-[#0B2545] border-slate-600 hover:bg-slate-700"
                >
                  <Edit className="w-4 h-4 mr-1" />
                  Editar
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setViewStickerModalOpen(false)}
                  className="text-slate-500 hover:text-[#0B2545]"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>
            
            {/* V6.2 FIX: Sticker Preview - Config-based (no server auth issues) */}
            <div className="flex items-center justify-center p-8 min-h-[450px]">
              {savedSticker ? (
                <div 
                  className="w-[350px] h-[350px] rounded-2xl shadow-2xl flex flex-col items-center justify-center relative overflow-hidden"
                  style={{ 
                    background: `linear-gradient(135deg, ${
                      savedSticker.config?.colors?.background_gradient?.colors?.[0] || 
                      savedSticker.config?.backgroundGradient?.colors?.[0] || 
                      '#10B981'
                    } 0%, ${
                      savedSticker.config?.colors?.background_gradient?.colors?.[1] || 
                      savedSticker.config?.backgroundGradient?.colors?.[1] || 
                      '#059669'
                    } 100%)`
                  }}
                >
                  {/* Frame border */}
                  <div className="absolute inset-3 border-2 border-white/25 rounded-xl pointer-events-none" />
                  
                  {/* Sticker Content */}
                  <div className="text-center text-[#0B2545] px-6 z-10">
                    {/* Title */}
                    <h3 className="font-extrabold text-2xl line-clamp-2 drop-shadow-lg mb-2">
                      {savedTexts.title || vehicle?.name}
                    </h3>
                    {/* Subtitle */}
                    {savedTexts.subtitle && (
                      <p className="text-base text-slate-600 mb-1">
                        {savedTexts.subtitle}
                      </p>
                    )}
                    {/* Slogan */}
                    {savedTexts.slogan && (
                      <p className="text-sm text-white/90 font-medium mb-3">
                        {savedTexts.slogan}
                      </p>
                    )}
                    {/* QR Code */}
                    <div className="bg-white rounded-xl p-3 inline-block shadow-lg">
                      <QrCode className="w-20 h-20 text-slate-800" />
                    </div>
                    {/* AvisoCar text */}
                    <p className="text-sm mt-2 font-medium text-slate-600">AvisoCar.com</p>
                  </div>
                  
                  {/* Branding footer */}
                  <div className="absolute bottom-4 left-0 right-0 text-center">
                    <p className="text-base font-bold text-[#0B2545]">🚗 AvisoCar.com</p>
                    <p className="text-[10px] text-slate-500">Escanea el QR y contacta al dueño</p>
                  </div>
                  
                  {/* V6.4: Show emoji if enabled - with SAVED POSITION */}
                  {(savedSticker.config?.effects?.character_enabled || savedSticker.config?.characterEnabled) && 
                   (savedSticker.config?.effects?.character_id || savedSticker.config?.characterId) && (
                    <div 
                      className="absolute text-4xl drop-shadow-lg"
                      style={{
                        // V6.4: Use saved position if available
                        left: savedSticker.config?.layout_offsets?.emoji?.xPercent !== undefined 
                          ? `${savedSticker.config.layout_offsets.emoji.xPercent * 100}%`
                          : savedSticker.config?.layoutOffsets?.emoji?.xPercent !== undefined
                            ? `${savedSticker.config.layoutOffsets.emoji.xPercent * 100}%`
                            : 'auto',
                        top: savedSticker.config?.layout_offsets?.emoji?.yPercent !== undefined
                          ? `${savedSticker.config.layout_offsets.emoji.yPercent * 100}%`
                          : savedSticker.config?.layoutOffsets?.emoji?.yPercent !== undefined
                            ? `${savedSticker.config.layoutOffsets.emoji.yPercent * 100}%`
                            : 'auto',
                        right: (savedSticker.config?.layout_offsets?.emoji?.xPercent === undefined && 
                               savedSticker.config?.layoutOffsets?.emoji?.xPercent === undefined) ? '24px' : 'auto',
                        bottom: (savedSticker.config?.layout_offsets?.emoji?.yPercent === undefined &&
                                savedSticker.config?.layoutOffsets?.emoji?.yPercent === undefined) ? '64px' : 'auto'
                      }}
                    >
                      {(() => {
                        const charId = savedSticker.config?.effects?.character_id || savedSticker.config?.characterId;
                        // V6.5: Complete emoji map matching stickerCharacters.js
                        const emojiMap = {
                          // Creativos
                          'hero_smile': '😎', 'oops_face': '😅', 'wink': '😉', 'thinking': '🤔', 'star_eyes': '🤩',
                          // Vehículos
                          'car_red': '🚗', 'car_sport': '🏎️', 'moto': '🏍️', 'truck': '🚚', 'parking': '🅿️',
                          // Emergencia
                          'siren': '🚨', 'ambulance': '🚑', 'warning': '⚠️', 'sos': '🆘', 'medical': '🏥',
                          // Mascotas
                          'dog': '🐕', 'cat': '🐱', 'paw': '🐾', 'puppy_face': '🐶',
                          // Niños
                          'child': '🧒', 'family': '👨‍👩‍👧', 'baby': '👶', 'angel': '👼',
                          // Viajes
                          'plane': '✈️', 'luggage': '🧳', 'backpack': '🎒', 'world': '🌍',
                          // Objetos
                          'key': '🔑', 'phone': '📱', 'gift': '🎁', 'heart': '❤️',
                          // Privacidad
                          'lock': '🔒', 'shield': '🛡️', 'spy': '🕵️',
                          // LATAM
                          'peru_flag': '🇵🇪', 'handshake': '🤝', 'fist': '👊', 'call_me': '🤙',
                          // Legacy IDs (backwards compatibility)
                          'car_wink': '🚗', 'car_cool': '😎', 'car_happy': '😊', 'hand_wave': '👋',
                          'sparkles': '✨', 'star': '⭐', 'thumbsup': '👍', 'smile': '😊',
                          'fire': '🔥', 'rocket': '🚀', 'check': '✅'
                        };
                        return emojiMap[charId] || '🚗';
                      })()}
                    </div>
                  )}
                  
                  {/* V6.4: Show icons if enabled - with SAVED POSITION */}
                  {(savedSticker.config?.icons?.show !== false && savedSticker.config?.showIcons !== false) && 
                   (savedSticker.config?.icons?.active?.length > 0 || savedSticker.config?.activeIcons?.length > 0) && (
                    <div 
                      className="absolute flex gap-1 bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-lg"
                      style={{
                        // V6.4: Use saved position if available
                        left: savedSticker.config?.layout_offsets?.icons?.xPercent !== undefined 
                          ? `${savedSticker.config.layout_offsets.icons.xPercent * 100}%`
                          : savedSticker.config?.layoutOffsets?.icons?.xPercent !== undefined
                            ? `${savedSticker.config.layoutOffsets.icons.xPercent * 100}%`
                            : 'auto',
                        top: savedSticker.config?.layout_offsets?.icons?.yPercent !== undefined
                          ? `${savedSticker.config.layout_offsets.icons.yPercent * 100}%`
                          : savedSticker.config?.layoutOffsets?.icons?.yPercent !== undefined
                            ? `${savedSticker.config.layoutOffsets.icons.yPercent * 100}%`
                            : '24px',
                        right: (savedSticker.config?.layout_offsets?.icons?.xPercent === undefined && 
                               savedSticker.config?.layoutOffsets?.icons?.xPercent === undefined) ? '24px' : 'auto'
                      }}
                    >
                      {(savedSticker.config?.icons?.active || savedSticker.config?.activeIcons || []).slice(0, 3).map((iconId, i) => {
                        // V6.3: Complete icon map matching editor's PREMIUM_ICONS
                        const iconMap = {
                          // Vehiculos
                          'car': '🚗', 'tow': '🚛', 'parking': '🅿️', 'lights': '🚨', 'alarm': '🔔',
                          // Niños
                          'child': '🧒', 'family': '👨‍👩‍👧', 'school': '🎒', 'park': '🎠', 'bracelet': '📿',
                          // Mascotas
                          'paw': '🐾', 'collar': '🦮', 'pet_home': '🏠', 'vet': '💉', 'bone': '🦴',
                          // Objetos
                          'luggage': '🧳', 'airport': '✈️', 'backpack': '🎒', 'keys': '🔑', 'wallet': '👛',
                          // Viajes
                          'plane': '✈️', 'passport': '🛂', 'map': '🗺️', 'hotel': '🏨', 'ticket': '🎫',
                          // Emergencias
                          'sos': '🆘', 'ambulance': '🚑', 'crash': '💥', 'police': '🚔', 'fire': '🔥',
                          // Privacidad
                          'lock': '🔒', 'shield': '🛡️', 'anonymous': '🎭', 'eye_off': '🙈', 'secure': '🔐',
                          // Legacy icons
                          'motorcycle': '🏍️', 'truck': '🚛', 'bike': '🚲', 'bus': '🚌',
                          'heart': '❤️', 'star': '⭐', 'lightning': '⚡', 'sun': '☀️', 'moon': '🌙',
                          'key': '🔑', 'bell': '🔔'
                        };
                        return <span key={i} className="text-xl">{iconMap[iconId] || '⭐'}</span>;
                      })}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center text-slate-500">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
                  <p>{t("vd_sticker.loading")}</p>
                </div>
              )}
            </div>
            
            {/* Footer with Download Buttons - V6.3 FIX: Direct download instead of opening editor */}
            <div className="p-4 border-t border-slate-700 flex items-center justify-center gap-4">
              <Button
                onClick={() => handleDirectDownload('png')}
                disabled={isDownloading || !!vehicle?.qr_locked}
                variant="outline"
                className="text-[#0B2545] border-slate-600 hover:bg-slate-700 disabled:opacity-50"
              >
                {isDownloading && downloadFormat === 'png' ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                {t("vd_sticker.download_png")}
              </Button>
              <Button
                onClick={() => handleDirectDownload('pdf')}
                disabled={isDownloading || !!vehicle?.qr_locked}
                variant="outline"
                className="text-[#0B2545] border-slate-600 hover:bg-slate-700 disabled:opacity-50"
              >
                {isDownloading && downloadFormat === 'pdf' ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <FileText className="w-4 h-4 mr-2" />
                )}
                Descargar PDF
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Public Alias (privacy-safe display name) */}
      <div className="mt-6">
        <PublicAliasConfig vehicleId={id} />
      </div>

      {/* iter71 4.1 — pausar avisos de este vehículo */}
      <div className="mt-6">
        <PauseNoticesConfig vehicleId={id} />

        <div className="mt-4">
          <RotateQRConfig
            vehicleId={id}
            onRotated={() => {
              setRotatedBanner(true);
              setQrRefreshKey(Date.now());
              fetchVehicle();
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          />
        </div>
      </div>

      {/* iter64 — Emergency contacts feature purged. Emergencia flow (scanner button) sigue vivo vía push al propietario. */}
    </div>
  );
};

export default VehicleDetail;
